import { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send cookies on every same-origin/cross-origin request so the httpOnly
// `access_token` cookie set by the backend during login is included on
// subsequent requests. CORS already sends `Access-Control-Allow-Credentials`.
axios.defaults.withCredentials = true;

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// Best-effort client-side JWT expiry check. Returns true if the token has an `exp` claim in the past.
// Also returns true if the token is falsy/malformed — treat as no valid session.
const _isTokenExpired = (t) => {
  if (!t || typeof t !== "string") return true;
  try {
    const payload = JSON.parse(atob(t.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (!payload.exp) return false;
    return payload.exp * 1000 < Date.now();
  } catch { return true; } // malformed token → treat as expired
};

// Every localStorage key that can carry an old user's identity. Cleared in
// bulk on every login and logout so a second user on the same browser can
// never see stale state from the first.
const _AUTH_KEYS = [
  "token", "user", "wallet_token", "wallet_user",
  "member_reg_token", "member_reg_fee", "member_reg_member_id",
  "member_reg_is_setup",
];

const _purgeAuthStorage = () => {
  _AUTH_KEYS.forEach(k => { try { localStorage.removeItem(k); } catch { /* private mode */ } });
};

// Read token/user from either key pair. Kept in sync across the codebase during migration.
const readInitialToken = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("wallet_token");
  if (_isTokenExpired(t)) {
    // Purge stale session so ShopHeader/UI don't show a phantom "Hi, Name" pill.
    _purgeAuthStorage();
    return null;
  }
  return t;
};
const readInitialUser = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("wallet_token");
  if (_isTokenExpired(t)) return null;    // No valid token → no user, even if user JSON is present.
  try {
    const raw = localStorage.getItem("user") || localStorage.getItem("wallet_user");
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
};

// Two users are "the same identity" iff any of the id fields match. Used to
// decide whether login() should hard-reload the page to shed cached React
// state left over from the previous user.
const _sameIdentity = (a, b) => {
  if (!a || !b) return false;
  const keys = ["id", "customer_id", "member_id", "guest_id", "email"];
  return keys.some(k => a[k] && b[k] && String(a[k]).toLowerCase() === String(b[k]).toLowerCase());
};

export const AuthProvider = ({ children }) => {
  // Initialize synchronously so a hard refresh on a protected page (e.g. checkout) sees the user immediately
  const [token, setToken] = useState(() => readInitialToken());
  const [user, setUser] = useState(() => readInitialUser());

  // Cross-tab sync: if another tab logs someone else in, this tab reloads to
  // shed any React state (dashboard data, cart, etc.) that belonged to the
  // previous user. The `storage` event only fires in OTHER tabs, which is
  // exactly what we want here.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== "user" && e.key !== "wallet_user" && e.key !== "token" && e.key !== "wallet_token") return;
      const newToken = readInitialToken();
      const newUser = readInitialUser();
      const prevUser = user;
      setToken(newToken);
      setUser(newUser);
      // Identity changed (login as different user in another tab, or logout).
      // Any cached dashboard state in this tab is now stale — reload it.
      if ((prevUser && !newUser) || (prevUser && newUser && !_sameIdentity(prevUser, newUser))) {
        window.location.reload();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const login = (tokenValue, userData) => {
    // Identity guard: if this login is for a DIFFERENT user than the one
    // currently in memory / localStorage, purge every stale key first AND
    // force a hard reload so no cached React state (dashboard payload, cart,
    // family-member list, etc.) from the previous user survives.
    const prevRaw = localStorage.getItem("user") || localStorage.getItem("wallet_user");
    let prevUser = null;
    try { prevUser = prevRaw ? JSON.parse(prevRaw) : null; } catch { /* ignore */ }
    const identityChanged = prevUser && !_sameIdentity(prevUser, userData);

    _purgeAuthStorage();
    localStorage.setItem("token", tokenValue);
    localStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("wallet_token", tokenValue);
    localStorage.setItem("wallet_user", JSON.stringify(userData));
    setToken(tokenValue);
    setUser(userData);

    if (identityChanged) {
      // Give the caller a tick to navigate() to the new dashboard first, then
      // reload so the new mount is fresh. Without this, the previous user's
      // dashboard would render for a frame before the fetch replaced it.
      setTimeout(() => { window.location.reload(); }, 50);
    }
  };

  const logout = async () => {
    // Best-effort server-side cookie clear. Every role has its own logout
    // endpoint — we try them all in parallel; whichever one matches the
    // current session succeeds, the others 401/403 silently.
    const endpoints = [
      "/wallet/logout",
      "/finance-admin/logout",
      "/accountant/logout",
      "/merchant/logout",
      "/shop-admin/logout",
      "/admin/logout",
    ];
    await Promise.allSettled(
      endpoints.map(ep => axios.post(`${API}${ep}`, {}, { withCredentials: true }))
    );
    _purgeAuthStorage();
    setToken(null);
    setUser(null);
  };

  // Every request from authAxios carries the cookie automatically (withCredentials).
  // We continue to send the Bearer header too so legacy callers keep working
  // — the backend prefers the cookie, so this is safe.
  const authAxios = axios.create({
    baseURL: API,
    withCredentials: true,
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  useEffect(() => {
    // iter129 forced-logout wiring: when the backend refuses a session
    // (FA-triggered state change stamped `session_invalidated_at` after
    // the JWT was issued), we get a 401 with detail "Session invalidated
    // by admin\u2026". Kick the user back to /login without letting the
    // stale cookie linger \u2014 so a subsequent request cannot silently
    // re-use it under the wrong role.
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        try {
          const detail = err?.response?.data?.detail || "";
          if (err?.response?.status === 401 && typeof detail === "string" && detail.toLowerCase().includes("session invalidated")) {
            _purgeAuthStorage();
            setToken(null);
            setUser(null);
            // Hard reload so React state resets and the login page mounts
            // without any lingering fetches from the old identity.
            const slug = (window.location.pathname.split("/")[1] || "mymaitree");
            window.location.replace(`/${slug}/login?forced=1`);
          }
        } catch { /* ignore */ }
        return Promise.reject(err);
      },
    );
    return () => { axios.interceptors.response.eject(id); };
  }, []);

  return <AuthContext.Provider value={{ user, token, login, logout, authAxios }}>{children}</AuthContext.Provider>;
};
